import type { EventType } from './types';

/**
 * Tipos de evento. Cada uno es una página con contenido propio: por qué una
 * cabina funciona en ESE evento, en qué momento abrirla, qué equipo rinde
 * mejor y qué preguntan siempre los clientes de ese tipo.
 *
 * Las fotos están asignadas una por una tras revisarlas visualmente. No se
 * asignan por el nombre del archivo: en este banco hay nombres que no
 * corresponden con lo que muestra la imagen.
 */
export const EVENT_TYPES: EventType[] = [
  {
    slug: 'bodas',
    name: 'Bodas',
    shortName: 'Boda',
    tagline: 'Lo que el fotógrafo no alcanza a ver',
    seoTitle: 'Cabina de Fotos para Bodas | Alquiler con Libro de Firmas',
    seoDescription:
      'Cabina de fotos para matrimonios: marco con los nombres, doble copia para el álbum de firmas e impresión ilimitada. Montaje en salones y fincas.',
    keywords:
      'cabina de fotos para bodas, cabina de fotos matrimonio, fotocabina boda, libro de firmas con fotos, cabina de fotos boda campestre',
    heroImage: '/images/social/novios-tirillas-boda-jardin-cabina.jpg',
    heroAlt: 'Los novios sosteniendo sus tiras de fotos recién impresas en el jardín de la recepción',
    stripImage: '/images/social/tirilla-boda-marco-personalizado-cabina.jpg',
    stripAlt: 'Tira de fotos con el marco personalizado de una boda: los nombres de los novios y la fecha',
    intro:
      'El fotógrafo que contrataste cubre la ceremonia, el protocolo y los retratos. Todo eso queda impecable. Lo que casi nunca queda registrado es lo que pasa después: cuando se afloja la corbata, la tía se pone unas gafas de plástico y alguien se sube a la mesa.',
    intro2:
      'Esa parte de la noche es la que los novios más ven después, y es exactamente la que recoge una cabina. No compite con el fotógrafo: cubre lo que él no puede estar mirando porque está haciendo otra cosa.',
    aiQuestion:
      '¿Qué cabina de fotos es mejor para una boda?',
    aiAnswer:
      'Para una boda la elección habitual es un espejo fotográfico o la cabina en madera Wally: ambos permiten fotos de grupo completo y se integran a la decoración en lugar de competir con ella. Lo que más valoran los novios es la doble copia, porque una se la lleva el invitado y la otra se pega en el álbum de firmas junto a su mensaje. El alquiler incluye operador, impresión ilimitada y el marco diseñado con los nombres y la fecha.',
    why: [
      {
        title: 'Se lleva algo a casa',
        text: 'Un archivo digital se pierde entre mil fotos del celular. Una tira impresa termina en la nevera o en la billetera. Es el único recuerdo del evento que el invitado se lleva puesto esa misma noche.',
      },
      {
        title: 'Reemplaza el libro de firmas',
        text: 'Se imprime doble copia: una se la lleva el invitado y la otra se pega en un álbum donde escribe el mensaje al lado de su foto. Los novios terminan con un libro que sí se vuelve a abrir.',
      },
      {
        title: 'Entretiene el tiempo muerto',
        text: 'Entre la ceremonia y la cena hay un hueco donde los novios están en la sesión de fotos y los invitados no saben qué hacer. La cabina llena exactamente ese hueco.',
      },
    ],
    moments: [
      {
        title: 'Durante el cóctel',
        text: 'Mientras los novios están en la sesión de fotos, los invitados ya tienen algo que hacer y empiezan a mezclarse entre mesas.',
      },
      {
        title: 'Al abrir la pista',
        text: 'Terminada la cena, la cabina se vuelve el segundo punto de reunión del salón: la gente entra en grupos y sale con la tira en la mano.',
      },
      {
        title: 'En la hora loca',
        text: 'Con los props y el desorden de la hora loca salen las fotos que después circulan por todos los chats de la familia.',
      },
    ],
    recommended: ['wally', 'mirror-black', 'plataforma-360', 'cabina-telefonica'],
    faqs: [
      { q: '¿En qué momento conviene abrir la cabina?', a: 'Lo más común es al terminar la cena, cuando arranca la pista. Si el cóctel es largo también funciona muy bien ahí, porque entretiene mientras los novios están en la sesión de fotos.' },
      { q: '¿Cuántas horas se contratan para una boda?', a: 'Entre tres y cuatro horas cubren bien el grueso de la celebración. Si la fiesta se extiende, se pueden sumar horas el mismo día.' },
      { q: '¿La cabina reemplaza al fotógrafo?', a: 'No, hacen cosas distintas. El fotógrafo cubre la ceremonia, el protocolo y los retratos; la cabina recoge lo espontáneo, que es lo que normalmente no queda registrado.' },
      { q: '¿Sirve como libro de firmas?', a: 'Sí, y es de lo que más recomendamos. Doble copia: una para el invitado y otra para el álbum, donde escribe el mensaje junto a su foto.' },
      { q: '¿Pueden montar en una finca fuera de la ciudad?', a: 'Sí. Solo necesitamos confirmar antes el punto eléctrico y si el acceso final es destapado, porque eso cambia el tiempo de montaje.' },
      { q: '¿Y si es al aire libre y llueve?', a: 'Acordamos desde antes una ubicación alterna bajo techo o carpa. Los equipos son electrónicos y no pueden quedar expuestos, así que ese plan B hace parte de la coordinación previa.' },
    ],
    gallery: [
      { src: '/images/social/novia-espejo-magico-boda-cabina.jpg', alt: 'Novia posando con props frente al espejo mágico bajo un letrero de neón' },
      { src: '/images/social/montaje-cabina-jardin-boda-cabina.jpg', alt: 'Cabina con sombrilla difusora montada en el jardín de una boda con luces colgantes' },
      { src: '/images/social/tirilla-boda-familia-cabina.jpg', alt: 'Tira de fotos de boda con el mensaje impreso "gracias por hacer parte de nuestra historia"' },
      { src: '/images/social/invitadas-props-carpa-cabina.jpg', alt: 'Invitadas de una boda posando con gafas y carteles divertidos bajo la carpa' },
    ],
  },

  {
    slug: 'quince-anos',
    name: 'Quince años',
    shortName: 'Quince',
    tagline: 'Para una generación que publica en vivo',
    seoTitle: 'Cabina de Fotos para Quince Años | Alquiler y Video 360',
    seoDescription:
      'Cabina de fotos y cabina 360 para fiestas de quince años. Tira personalizada con el nombre de la quinceañera y video listo para publicar en el momento.',
    keywords:
      'cabina de fotos quince años, cabina 360 quince años, fotocabina 15 años, tirillas quince años, photobooth quinceañera',
    heroImage: '/images/social/quinceanera-amigos-fiesta-cabina.jpg',
    heroAlt: 'La quinceañera con su corona rodeada de amigos frente a la cabina de fotos',
    stripImage: '/images/social/tirillas-quince-anos-cabina.jpg',
    stripAlt: 'Tiras de fotos personalizadas con el diseño de una fiesta de quince años',
    intro:
      'Los quince tienen un público que documenta todo mientras sucede. No basta con que la foto quede bonita: tiene que estar en el celular antes de que termine la fiesta, porque si no llega esa noche, ya no llega.',
    intro2:
      'Por eso en estos eventos el video pesa más que la impresión, y la fila deja de ser una espera para volverse parte del show. La quinceañera suele pasar por la cabina cinco o seis veces durante la noche, con grupos distintos cada vez.',
    aiQuestion:
      '¿Qué conviene más para unos quince años: cabina de fotos o cabina 360?',
    aiAnswer:
      'Para una fiesta de quince años la Plataforma 360 es la más pedida, porque entrega un video vertical editado en menos de un minuto y listo para publicar durante la fiesta, que es cuando de verdad circula. La combinación ideal, si el presupuesto lo permite, es 360 más una estación de foto impresa con el nombre de la quinceañera en la tira. Por encima de 150 invitados conviene montar dos estaciones.',
    why: [
      {
        title: 'Contenido inmediato',
        text: 'El video sale editado, con música y en cámara lenta, en menos de un minuto. Se publica sin salir del salón, que es cuando de verdad tiene alcance.',
      },
      {
        title: 'La fila es parte de la fiesta',
        text: 'A diferencia de otros eventos, aquí hacer cola no molesta: los grupos se organizan, ensayan la pose y se graban entre ellos mientras esperan.',
      },
      {
        title: 'La tira con su nombre',
        text: 'El marco se diseña con el nombre y la fecha. Es el detalle que convierte la foto en un recuerdo fechado y no en una más del carrete.',
      },
    ],
    moments: [
      {
        title: 'Antes del vals',
        text: 'Mientras llegan los invitados y se acomodan las mesas, la cabina evita el silencio incómodo del principio.',
      },
      {
        title: 'Después del protocolo',
        text: 'Terminado el vals y los discursos, se abre la pista y la cabina absorbe a todos los que todavía no se animan a bailar.',
      },
      {
        title: 'En la hora loca',
        text: 'Es el pico absoluto de uso. Conviene tener el equipo con papel de sobra y, si son más de 150 invitados, dos estaciones.',
      },
    ],
    recommended: ['plataforma-360', 'mirror-glam', 'ring', 'magazine'],
    faqs: [
      { q: '¿Qué conviene más para unos quince: foto o video?', a: 'Video. La Plataforma 360 es la más pedida porque entrega un clip vertical listo para publicar, que es el formato que este público usa. Si el presupuesto alcanza, la combinación ideal es 360 más una estación de foto impresa.' },
      { q: '¿Se puede personalizar con el nombre y los colores?', a: 'Sí. El marco de la tira y la pantalla se diseñan con el nombre, la fecha y la paleta de la fiesta, y se aprueban antes del evento.' },
      { q: '¿Puedo elegir la música del video 360?', a: 'Sí, y vale la pena definirla con anticipación: la canción del clip es la que va a acompañar todo lo que se publique esa noche.' },
      { q: '¿Cuántos invitados aguanta?', a: 'Depende del formato. Los espejos y el 360 mueven mucha gente por hora; una cabina cerrada es más lenta. Por encima de 150 invitados recomendamos dos estaciones.' },
    ],
    gallery: [
      { src: '/images/social/quinceanera-decoracion-flores-cabina.jpg', alt: 'La quinceañera y una amiga posando abrazadas junto a la decoración floral' },
      { src: '/images/social/amigas-props-fiesta-cumpleanos-cabina.jpg', alt: 'Grupo de amigas posando con orejas y accesorios en la cabina de fotos' },
      { src: '/images/social/invitados-props-pared-flores-cabina.jpg', alt: 'Invitados posando con gafas y antifaces frente a una pared de flores' },
      { src: '/images/Plataforma360.jpg', alt: 'Plataforma de video 360 instalada y lista antes de que empiece la fiesta' },
    ],
  },

  {
    slug: 'cumpleanos',
    name: 'Cumpleaños',
    shortName: 'Cumpleaños',
    tagline: 'Funciona en un salón o en la sala de la casa',
    seoTitle: 'Cabina de Fotos para Cumpleaños | Alquiler por Horas',
    seoDescription:
      'Cabina de fotos para cumpleaños y fiestas privadas. Formatos compactos que entran en espacios pequeños, con impresión ilimitada y entrega digital.',
    keywords:
      'cabina de fotos cumpleaños, fotocabina fiesta, cabina de fotos para fiesta en casa, alquiler cabina de fotos por horas',
    heroImage: '/images/social/reunion-familiar-props-invitados-cabina.jpg',
    heroAlt: 'Grupo de invitados de todas las edades posando con sombreros y gafas en un cumpleaños',
    stripImage: '/images/photoboothtiras.jpg',
    stripAlt: 'Tiras de fotos impresas recién salidas de la cabina',
    intro:
      'Los cumpleaños son el evento más variable que atendemos: van desde una reunión de veinte personas en una casa hasta una fiesta de doscientas en un salón. Lo que casi siempre se repite es que el espacio es más ajustado y la logística más informal que en una boda.',
    intro2:
      'Por eso aquí el montaje se adapta en vez de imponerse. Antes de cotizar preguntamos dónde va a estar el equipo, por dónde entra y dónde está la toma de corriente más cercana: en apartamentos, un ascensor pequeño o una escalera estrecha cambian qué cabina podemos subir.',
    aiQuestion:
      '¿Qué cabina de fotos sirve para un cumpleaños en un espacio pequeño?',
    aiAnswer:
      'Para cumpleaños en casa o en salones pequeños el formato de aro de luz es el indicado: ocupa cerca de metro y medio, se instala en unos veinticinco minutos y necesita solo una toma eléctrica cerca. Se contrata por horas —tres suelen bastar— e incluye props, operador e impresión ilimitada. Antes de cotizar conviene revisar cómo es el acceso, porque escaleras estrechas o ascensores pequeños definen qué equipo se puede subir.',
    why: [
      {
        title: 'Entra donde no cabe otra cosa',
        text: 'El formato de aro de luz ocupa metro y medio y se instala en veinticinco minutos. En una sala despejada es suficiente.',
      },
      {
        title: 'Rompe el hielo temprano',
        text: 'En una fiesta con grupos que no se conocen entre sí, la cabina es la excusa para que se mezclen sin que nadie tenga que presentarlos.',
      },
      {
        title: 'Se contrata por horas',
        text: 'No hace falta cubrir toda la fiesta. Tres horas bien ubicadas —cuando ya llegó todo el mundo— cubren el grueso de los invitados.',
      },
    ],
    moments: [
      {
        title: 'Cuando ya llegó todo el mundo',
        text: 'Abrirla desde el minuto uno desperdicia horas contratadas. El punto óptimo es cuando el salón ya está lleno.',
      },
      {
        title: 'Después del brindis',
        text: 'Terminado el momento formal, la gente se dispersa y busca qué hacer. Ahí la cabina concentra al grupo otra vez.',
      },
      {
        title: 'Al final de la noche',
        text: 'Las últimas fotos, con la fiesta ya suelta, suelen ser las mejores del evento.',
      },
    ],
    recommended: ['ring', 'cabina-extreme', 'mirror-lux', 'cabina-espejada'],
    faqs: [
      { q: '¿Sirve para una fiesta en un apartamento?', a: 'Sí, con el formato compacto. Necesitamos metro y medio libre, una toma cerca y saber de antemano cómo es el acceso: escaleras estrechas o ascensores pequeños definen qué equipo podemos subir.' },
      { q: '¿Cuál es el mínimo de horas?', a: 'El estándar son tres horas de operación. Para eventos pequeños se puede ajustar; coméntalo al cotizar.' },
      { q: '¿Incluye props?', a: 'Sí, todos los formatos llegan con accesorios. Si la fiesta tiene una temática concreta, se pueden preparar props acordes.' },
    ],
    gallery: [
      { src: '/images/social/amigas-props-fiesta-cumpleanos-cabina.jpg', alt: 'Amigas posando con accesorios divertidos durante una fiesta de cumpleaños' },
      { src: '/images/social/invitadas-props-carpa-cabina.jpg', alt: 'Invitadas con gafas y carteles posando bajo la carpa de la fiesta' },
      { src: '/images/KuvaRing.jpg', alt: 'Cabina compacta de aro de luz, el formato que entra en espacios reducidos' },
      { src: '/images/social/invitados-props-pared-flores-cabina.jpg', alt: 'Grupo de invitados posando con props frente a una pared de flores' },
    ],
  },

  {
    slug: 'eventos-empresariales',
    name: 'Eventos empresariales',
    shortName: 'Empresarial',
    tagline: 'Cuando el evento tiene que rendir cuentas',
    seoTitle: 'Cabina de Fotos para Eventos Empresariales | Alquiler',
    seoDescription:
      'Cabina de fotos para fiestas de fin de año, integraciones y eventos internos de empresa. Marco con la identidad corporativa y reporte de participación.',
    keywords:
      'cabina de fotos eventos empresariales, cabina de fotos fin de año empresa, fotocabina corporativa, cabina de fotos integracion empresa',
    heroImage: '/images/social/evento-empresarial-cabina-fotos-cabina.jpg',
    heroAlt: 'Colaboradores con escarapela posando junto a la cabina en un evento de empresa',
    stripImage: '/images/corporate/event_11.jpg',
    stripAlt: 'Tira de fotos personalizada con la identidad visual de una empresa cliente',
    intro:
      'Un evento de empresa no se mide en risas sino en asistencia, participación y material aprovechable después. Esa es la diferencia real con un evento social, y cambia lo que hay que entregar al terminar.',
    intro2:
      'Aquí la cabina cumple tres funciones a la vez: entretiene, produce contenido con la identidad de la compañía y deja un registro de cuánta gente participó. Nos lo piden sobre todo para fiestas de fin de año, integraciones, aniversarios de la empresa y premiaciones internas.',
    aiQuestion:
      '¿Qué cabina de fotos conviene para un evento de empresa?',
    aiAnswer:
      'Para eventos de empresa se recomiendan los formatos abiertos —espejo fotográfico o plataforma 360— porque atienden más asistentes por hora que una cabina cerrada. Se personalizan el marco impreso, la pantalla y el fondo con la identidad de la compañía, y con el módulo de registro se entrega al cierre la base de datos de quienes participaron y autorizaron el tratamiento de sus datos. Se factura electrónicamente a nombre de la empresa.',
    why: [
      {
        title: 'La marca en cada pieza',
        text: 'Marco, pantalla y fondo llevan la identidad de la compañía. Cada foto que sale es una pieza de comunicación interna que el colaborador se lleva por gusto, no por obligación.',
      },
      {
        title: 'Registro de participación',
        text: 'Con el módulo de registro, el asistente completa un formulario en pantalla antes de recibir su foto. Al cierre se entrega el archivo con quienes participaron.',
      },
      {
        title: 'Facturación en regla',
        text: 'Factura electrónica, órdenes de servicio y los tiempos que exigen los procesos de compra. Es la parte aburrida, pero es la que traba un evento corporativo.',
      },
    ],
    moments: [
      {
        title: 'En la entrada',
        text: 'Ubicada al ingreso funciona como registro y como rompehielos: la gente llega, se toma la foto y entra ya conversando.',
      },
      {
        title: 'Después de la cena',
        text: 'Terminada la parte protocolaria, la cabina absorbe a quienes no bailan y evita que la gente se vaya temprano.',
      },
      {
        title: 'Durante la premiación',
        text: 'Los reconocimientos internos quedan documentados con la marca de la compañía en el marco.',
      },
    ],
    recommended: ['mirror-lux', 'plataforma-360', 'cabina-soft', 'ring'],
    faqs: [
      { q: '¿Personalizan la cabina con nuestra marca?', a: 'Sí, es lo habitual en eventos de empresa. Personalizamos el marco impreso, la pantalla y el fondo, y enviamos la propuesta de diseño para aprobación antes del evento.' },
      { q: '¿Entregan la base de datos de los asistentes?', a: 'Sí, cuando se activa el módulo de registro. El asistente completa el formulario y autoriza el tratamiento de datos antes de recibir su foto; al cierre entregamos el archivo.' },
      { q: '¿Facturan a nombre de la empresa?', a: 'Sí, con factura electrónica. También manejamos órdenes de servicio y los tiempos de los procesos de compra.' },
      { q: '¿Cuántas personas alcanzan a pasar por hora?', a: 'Depende del formato: los espejos y los formatos abiertos mueven bastante más gente que una cabina cerrada. Por eso son los que recomendamos cuando el flujo es alto.' },
    ],
    gallery: [
      { src: '/images/corporate/event_5.png', alt: 'Equipo de trabajo posando junto en la cabina durante la fiesta de la empresa' },
      { src: '/images/social/evento-corporativo-espejo-noche-cabina.jpg', alt: 'Invitados de traje posando frente al espejo en una cena corporativa' },
      { src: '/images/social/activacion-navidena-centro-comercial-cabina.jpg', alt: 'Tiras de fotos de una activación navideña sostenidas frente al montaje' },
      { src: '/images/corporate/event_15.jpg', alt: 'Colaboradores uniformados posando en el evento anual de la compañía' },
    ],
  },

  {
    slug: 'activacion-de-marca',
    name: 'Activación de marca',
    shortName: 'Activación',
    tagline: 'La foto es el incentivo, el dato es el resultado',
    seoTitle: 'Cabina de Fotos para Activación de Marca | BTL',
    seoDescription:
      'Cabina de fotos para activaciones BTL, ferias, lanzamientos y punto de venta. Montaje con la marca, captura de datos y reporte de participación.',
    keywords:
      'activacion de marca cabina de fotos, btl cabina de fotos, cabina de fotos ferias, cabina de fotos punto de venta, photobooth activacion',
    heroImage: '/images/corporate/event_9.jpg',
    heroAlt: 'Clienta usando un espejo fotográfico con la marca durante una activación en punto de venta',
    stripImage: '/images/corporate/event_14.jpg',
    stripAlt: 'Tira de fotos con el diseño de la campaña, entregada al público en la activación',
    intro:
      'En una activación la foto no es el objetivo: es lo que hace que alguien se detenga, entregue sus datos y se quede el tiempo suficiente para escuchar el mensaje. Es la razón por la que una cabina funciona mejor que un volante.',
    intro2:
      'Hemos montado activaciones en ferias, centros comerciales, eventos deportivos y lanzamientos de producto. El montaje cambia mucho según el sitio: un stand tiene tres metros y un pasillo de centro comercial obliga a resolverlo todo en uno y medio.',
    aiQuestion:
      '¿Cómo funciona una cabina de fotos en una activación de marca?',
    aiAnswer:
      'En una activación BTL la cabina funciona como incentivo: el asistente entrega sus datos a cambio de su foto, lo que eleva la tasa de registro frente a un formulario suelto. La pieza impresa lleva la gráfica de la campaña y sigue circulando en redes días después del evento. Al cierre se entrega el número de participaciones, las piezas generadas y la base de datos. Un montaje compacto se resuelve en metro y medio; el 360 exige tres por tres.',
    why: [
      {
        title: 'Detiene al público',
        text: 'Un stand vacío no atrae. Un stand donde alguien se está tomando una foto y riéndose sí. La cabina genera la primera fila, y la fila atrae a la segunda.',
      },
      {
        title: 'Intercambio justo por el dato',
        text: 'La gente entrega su correo o su celular a cambio de algo que quiere: su foto. La tasa de registro es muy superior a la de un formulario suelto.',
      },
      {
        title: 'La pieza sigue circulando',
        text: 'La tira impresa lleva la marca y se la lleva el asistente. La copia digital se publica. Una activación de cuatro horas sigue apareciendo en redes días después.',
      },
    ],
    moments: [
      {
        title: 'Ferias y stands',
        text: 'La cabina da una razón concreta para entrar al stand y quedarse. El comercial conversa mientras la foto se imprime.',
      },
      {
        title: 'Punto de venta',
        text: 'Montaje compacto en pasillo o vitrina, con conteo de participaciones por jornada.',
      },
      {
        title: 'Lanzamientos',
        text: 'Piezas con la imagen del producto que los asistentes publican el mismo día del lanzamiento.',
      },
    ],
    recommended: ['mirror-lux', '180-aereo', 'ai-avatar', 'vogue-booth'],
    faqs: [
      { q: '¿Se puede llevar la misma activación a varias ciudades?', a: 'Sí, siempre que se planee con anticipación. Es lo normal en campañas nacionales que se replican en varias plazas el mismo fin de semana.' },
      { q: '¿Qué información entregan al final?', a: 'Número de participaciones, las piezas generadas y la base de datos de quienes autorizaron el registro. Se entrega al cierre de la activación.' },
      { q: '¿Cuánto espacio necesitan en un stand?', a: 'Depende del formato. El compacto resuelve en metro y medio; el 360 exige tres por tres libres y altura despejada. Si nos dicen las medidas del stand, recomendamos el que cabe.' },
      { q: '¿El montaje puede llevar la gráfica de la campaña?', a: 'Sí: marco, pantalla, fondo y el vinilo exterior del equipo se producen con la gráfica que nos entreguen.' },
    ],
    gallery: [
      { src: '/images/corporate/event_3.png', alt: 'Cabina con gráfica navideña montada en un centro comercial durante una campaña' },
      { src: '/images/corporate/event_1.jpg', alt: 'Tiras de fotos con la gráfica de la campaña entregadas al público asistente' },
      { src: '/images/corporate/event_7.png', alt: 'Público posando con la mascota de la marca durante una activación deportiva' },
      { src: '/images/corporate/event_10.jpg', alt: 'Montaje de cabina con la gráfica de aniversario de una marca de telecomunicaciones' },
    ],
  },

  {
    slug: 'reunion-familiar',
    name: 'Reunión familiar',
    shortName: 'Familiar',
    tagline: 'La foto que no se toma cuando están todos',
    seoTitle: 'Cabina de Fotos para Reuniones Familiares y Aniversarios',
    seoDescription:
      'Cabina de fotos para reuniones familiares, aniversarios, bautizos y grados. Impresión en el momento para que cada familia se lleve su copia.',
    keywords:
      'cabina de fotos reunion familiar, cabina de fotos aniversario, fotocabina grados, cabina de fotos bautizo, cabina de fotos familiar',
    heroImage: '/images/social/reunion-familiar-marco-personalizado-cabina.jpg',
    heroAlt: 'Familia numerosa posando junta frente al marco personalizado de la reunión',
    stripImage: '/images/social/tirillas-polaroid-familia-cabina.jpg',
    stripAlt: 'Tiras de fotos tipo polaroid con mensajes escritos a mano por la familia',
    intro:
      'En una reunión familiar siempre pasa lo mismo: se juntan personas que no se ven hace años y nadie toma la foto de todos juntos. Se toman veinte selfies por grupos y ninguna con la familia completa.',
    intro2:
      'Una cabina resuelve eso sin que nadie tenga que organizar a nadie. Funciona igual en un aniversario de bodas, un grado, un bautizo o un almuerzo de fin de año: la gente pasa por grupos, y al final existe el registro que de otra forma no habría existido.',
    aiQuestion:
      '¿Qué cabina de fotos funciona en una reunión familiar?',
    aiAnswer:
      'En reuniones familiares conviene un equipo con instrucciones simples en pantalla y operador acompañando, para que participen por igual los abuelos y los niños. Con impresión ilimitada cada núcleo de la familia se lleva su propia tira, sin tener que repartir fotos después. Para aniversarios y homenajes se suma el teléfono de mensajes de voz, que graba lo que cada invitado quiera decir y se entrega en audio al final.',
    why: [
      {
        title: 'Cada núcleo se lleva su copia',
        text: 'Con impresión ilimitada, cada rama de la familia sale con su tira. No hay que repartir después ni prometer que "las mando por WhatsApp".',
      },
      {
        title: 'Los mayores participan',
        text: 'Es de los pocos formatos que involucra a los abuelos igual que a los nietos. Basta con una pantalla que dé instrucciones claras y alguien del equipo acompañando.',
      },
      {
        title: 'Queda el mensaje, no solo la cara',
        text: 'Si se suma el teléfono de mensajes de voz, cada quien deja unas palabras. En un aniversario o un homenaje, eso pesa más que la foto.',
      },
    ],
    moments: [
      {
        title: 'Al llegar',
        text: 'La familia va llegando por tandas. Una cabina abierta desde el principio recoge a cada grupo apenas entra.',
      },
      {
        title: 'Después del almuerzo',
        text: 'Cuando ya todos se saludaron y la sobremesa se alarga, es el mejor momento para la foto de todos juntos.',
      },
      {
        title: 'Antes de que se vayan los mayores',
        text: 'Los abuelos suelen retirarse temprano. Conviene hacer la foto general antes de que empiece a faltar gente.',
      },
    ],
    recommended: ['cabina-telefonica', 'wally', 'mirror-lux', 'cabina-soft'],
    faqs: [
      { q: '¿Sirve para un grado o un bautizo?', a: 'Sí. Son eventos con la misma dinámica: mucha gente que se ve poco y un momento concreto que vale la pena registrar. Solo cambia el diseño del marco.' },
      { q: '¿Se puede personalizar con el apellido o la fecha?', a: 'Sí. El marco se diseña con el apellido, la fecha o el motivo de la reunión y se aprueba antes.' },
      { q: '¿Qué es el teléfono de mensajes?', a: 'Un teléfono antiguo adaptado para grabar: el invitado descuelga, escucha una indicación grabada por ustedes y deja su mensaje. Al final entregamos todos los audios.' },
    ],
    gallery: [
      { src: '/images/social/reunion-familiar-props-invitados-cabina.jpg', alt: 'Familia de varias generaciones posando junta con sombreros y accesorios' },
      { src: '/images/social/tirilla-boda-familia-cabina.jpg', alt: 'Tira de fotos familiar con el mensaje impreso de agradecimiento' },
      { src: '/images/innovators/cabina_telefonica.jpg', alt: 'Teléfono vintage adaptado para que los invitados graben mensajes de voz' },
      { src: '/images/social/tirillas-quince-anos-cabina.jpg', alt: 'Tiras de fotos impresas sostenidas por los invitados al final del evento' },
    ],
  },
];

export const getEventType = (slug?: string) => EVENT_TYPES.find((e) => e.slug === slug);
